import { createKeycloakUtils, isKeycloak } from 'oidc-spa/keycloak';
import { oidcSpa } from 'oidc-spa/react-spa';
import { z } from 'zod';

const decodedIdTokenSchema = z.object({
  sub: z.string(),
  name: z.string().optional(),
  preferred_username: z.string().optional(),
  email: z.string().email().optional(),
  picture: z.string().optional(),
  realm_access: z
    .object({
      roles: z.array(z.string()),
    })
    .optional(),
});

const decodedIdTokenMock = {
  sub: 'mock-user',
  name: 'John Doe',
  preferred_username: 'john.doe',
  email: 'john.doe@example.com',
  picture: 'https://example.com/avatar.jpg',
  realm_access: {
    roles: ['realm-admin'],
  },
};

const requiredEnv = (value: string | undefined, name: string): string => {
  if (value === undefined || value.trim() === '') {
    throw new Error(`Missing required OIDC environment variable: ${name}`);
  }

  return value.trim();
};

const normalizeIssuerUri = (value: string): string => value.replace(/\/+$/, '');

const issuerUri = normalizeIssuerUri(
  requiredEnv(import.meta.env.VITE_OIDC_ISSUER_URI, 'VITE_OIDC_ISSUER_URI'),
);
const clientId = requiredEnv(import.meta.env.VITE_OIDC_CLIENT_ID, 'VITE_OIDC_CLIENT_ID');

const extraScopes = import.meta.env.VITE_OIDC_SCOPES
  ? import.meta.env.VITE_OIDC_SCOPES.split(' ').filter(Boolean)
  : [];
const scopes = ['openid', 'profile', 'email', ...extraScopes];

const audience = import.meta.env.VITE_OIDC_AUDIENCE?.trim() || undefined;
const extraQueryParams = audience ? { audience } : undefined;

if (!isKeycloak({ issuerUri })) {
  throw new Error(
    'VITE_OIDC_ISSUER_URI must target a Keycloak realm, for example: https://auth.example.com/realms/myrealm',
  );
}

export const keycloakUtils = createKeycloakUtils({ issuerUri });

export const { bootstrapOidc, useOidc, getOidc, withLoginEnforced, OidcInitializationGate } =
  oidcSpa
    .withExpectedDecodedIdTokenShape({
      decodedIdTokenSchema,
      decodedIdToken_mock: decodedIdTokenMock,
    })
    .createUtils();

bootstrapOidc({
  implementation: 'real',
  issuerUri,
  clientId,
  scopes,
  extraQueryParams,
  debugLogs: import.meta.env.DEV,
});

export const loginRedirectUrl = '';

export type DecodedIdToken = z.infer<typeof decodedIdTokenSchema>;
